import React, { useEffect, useMemo, useState } from "react";
import type { Key, LegacyRef } from "react";
import { useHistory } from "react-router";
import { Row, Table, Tooltip } from "antd";
import { TableProps } from "antd/es/table";
import moment from "moment";
import type { Moment } from "moment";
import { useAppSelector } from "../../..";
import {
  checkIsLoading,
  getFio,
  toDisplayFormat,
  getDictionaryItemById,
  toHttpFormat,
} from "../../../lib/utils";
import type { TableColumn } from "../../../lib/interfaces";
import ToggleColumns from "../../ToggleableVersionsTable/ToggleColumns";
import type { LoadingStatusCode, Offer } from "../../../lib/types";
import {
  IOfferListSelectMode,
  IOfferListViewMode,
} from "../../../containers/OfferContainer/context/types";
import type { IOfferListSearch } from "../../../store/slices/offering/types";
import { useObserver } from "../utils/useObserver";
import "./OfferListTable.less";

interface TableData {
  key?: Key;
  id?: number;
  extName: string;
  name?: string;
  fromDate?: string;
  toDate?: string;
  offeringTypeName?: string;
  offeringStatusName?: string;
  isStandaloneName?: string;
  agreementNumber: string;
  agreementId?: number;
  agreementData: (string | number | undefined)[];
  prolongTypeName?: string;
  lifespanPeriodName?: string;
  period: string;
  tariffModelName?: string;
  serviceProfileName?: string;
  paymentTypeName?: string;
  invoicePeriodName?: string;
  isCurrentVersion: boolean;
  isPastVersion: boolean;
  scrollMarkIdx?: boolean; // Флаг для подгрузки по скроллу
}

interface ComponentProps {
  selectedOffer?: Offer<string>;
  setSelectedOffer: React.Dispatch<React.SetStateAction<Offer<string> | undefined>>;
  offerListViewMode: IOfferListViewMode;
  searchListValues: IOfferListSearch<Moment>;
  onChangeSearchValue: (field: Partial<IOfferListSearch<Moment>>) => void;
  onSearch: (paginationProps: { page: number; size?: number }) => void;
  offerList: Offer[];
  offerListTotalElements: number;
  offerListLoading: LoadingStatusCode;
  offerListSelectMode?: IOfferListSelectMode;
  filterName: string; // имя используемое как ключ localStorage для сохранения списка открытых полей поискового фильтра
  displayPastVersion: boolean; // В большинстве случае отображение истекших версий не требуется, если нужно - передать true
  setOfferListSelectMode: React.Dispatch<React.SetStateAction<IOfferListSelectMode>> | undefined;
}

/* ------------------------------------------------------ Начало компонента ------------------------------------------------------ */

export const OfferListTable = (props: ComponentProps) => {
  const {
    selectedOffer,
    setSelectedOffer,
    offerListViewMode,
    searchListValues,
    onChangeSearchValue,
    onSearch,
    offerList,
    offerListLoading,
    offerListTotalElements,
    offerListSelectMode,
    filterName,
    displayPastVersion,
    setOfferListSelectMode,
  } = props;
  const history = useHistory();
  const { offer } = useAppSelector((state) => state.offering);
  const {
    offeringAccessTypes,
    offeringAccessTypesLoading,
    offeringStatuses,
    offeringStatusesLoading,
    offeringProlongTypes,
    offeringProlongTypesLoading,
    lifespanPeriods,
    lifespanPeriodsLoading,
  } = useAppSelector((state) => state.dicts);

  const {
    tariffModelTypes,
    tariffModelTypesLoading,
    dicCurrenciesLoading,
    priceUnitsLoading,
    periodTypesLoading,
    tariffPeriodAlignmentsLoading,
    paymentType,
    paymentTypeLoading,
    invoicePeriodStatus,
    invoicePeriodStatusLoading,
  } = useAppSelector((state) => state.rtkDictionaries);

  const isTableLoading = useMemo(
    () =>
      checkIsLoading(
        offerListLoading,
        offeringAccessTypesLoading,
        offeringStatusesLoading,
        offeringProlongTypesLoading,
        lifespanPeriodsLoading,
        tariffModelTypesLoading,
        dicCurrenciesLoading,
        priceUnitsLoading,
        periodTypesLoading,
        tariffPeriodAlignmentsLoading,
        paymentTypeLoading,
        invoicePeriodStatusLoading
      ),
    [
      offerListLoading,
      offeringAccessTypesLoading,
      offeringStatusesLoading,
      offeringProlongTypesLoading,
      lifespanPeriodsLoading,
      tariffModelTypesLoading,
      dicCurrenciesLoading,
      priceUnitsLoading,
      periodTypesLoading,
      tariffPeriodAlignmentsLoading,
      paymentTypeLoading,
      invoicePeriodStatusLoading,
    ]
  );

  const scrollingRef = (useObserver({
    callback: () => {
      if ((searchListValues.page + 1) * searchListValues.size < offerListTotalElements) {
        const newPage = searchListValues.page + 1;
        onSearch({ page: newPage });
        onChangeSearchValue({ page: newPage });
      }
    },
    isLoading: isTableLoading,
  }) as unknown) as LegacyRef<HTMLDivElement>;

  const tableData = useMemo((): TableData[] => {
    const dataItems: TableData[] = offerList.map((offerItem) => {
      const { fromDate, toDate } = offerItem;
      const displayFromDate = fromDate && toDisplayFormat(fromDate);
      const displayToDate = toDate && toDisplayFormat(toDate);
      const agreementNumber = offerItem?.agreement?.agreementNumber || "";
      const fio = offerItem?.agreement ? getFio(offerItem.agreement.account.individual) : "";
      const clientName = offerItem?.agreement?.account?.organization?.fullName || fio;
      const agreementData = [clientName, agreementNumber, offerItem?.agreement?.id];
      const period = [displayFromDate, displayToDate].filter(Boolean).join(" - ");
      const isCurrentVersion = moment().isBetween(fromDate, toDate, "day", "[]");
      const isPastVersion = searchListValues.onDate
        ? moment(searchListValues.onDate).isAfter(toDate, "day")
        : moment().isAfter(toDate, "day");
      return {
        key: `${offerItem.id}_${offerItem.fromDate}`,
        id: offerItem.id,
        extName: offerItem.extName,
        name: offerItem.name,
        offeringTypeName: getDictionaryItemById(offeringAccessTypes, offerItem.offeringTypeId)
          ?.name,
        offeringStatusName: getDictionaryItemById(offeringStatuses, offerItem.offeringStatusId)
          ?.name,
        isStandaloneName: offerItem.isStandalone ? "Да" : "Нет",
        agreementNumber,
        agreementId: offerItem?.agreement?.id || offerItem?.agreementId,
        agreementData,
        prolongTypeName: getDictionaryItemById(offeringProlongTypes, offerItem.prolongTypeId)?.name,
        lifespanPeriodName: getDictionaryItemById(lifespanPeriods, offerItem.lifespanPeriodId)
          ?.name,
        period,
        tariffModelName: getDictionaryItemById(tariffModelTypes, offerItem.tariffModelId)?.name,
        serviceProfileName: offerItem.serviceProfile.name,
        paymentTypeName: getDictionaryItemById(paymentType, offerItem.serviceProfile.paymentTypeId)
          ?.name,
        invoicePeriodName: getDictionaryItemById(
          invoicePeriodStatus,
          offerItem.serviceProfile.invoicePeriodId
        )?.name,
        isCurrentVersion,
        isPastVersion,
        fromDate,
        toDate,
      };
    });
    const data: TableData[] = dataItems
      // Истекшие не выводим (если displayPastVersion false), если есть текущая только ее, если нет - то все будущие
      .filter(({ id, isCurrentVersion, isPastVersion }) => {
        if (isPastVersion) return displayPastVersion;
        if (isCurrentVersion) return true;
        const hasCurrentVersion =
          dataItems.filter((item) => item.id === id && item.isCurrentVersion).length === 1;
        return !hasCurrentVersion;
      })
      .filter(({ id }) => {
        if (
          (offerListViewMode === "selectOffer" ||
            offerListViewMode === "addTemplate" ||
            offerListViewMode === "selectOfferAdd") &&
          offer?.id
        ) {
          return id !== offer.id;
        }
        return true;
      });
    const sortedData = [...data].sort((a, b) => {
      if (a.id === b.id) {
        return moment(a.fromDate).isBefore(b.fromDate) ? 1 : -1;
      }
      return a.id! - b.id!;
    });
    return sortedData;
  }, [offerList, offerListViewMode, offer?.id]);

  const scrollMarkIdx = useMemo<number | undefined>(() => {
    return tableData.length - 2;
  }, [tableData.length]);

  const columnsWidths = {
    id: "50px",
    extName: "150px",
    name: "100px",
    offeringTypeName: "100px",
    offeringStatusName: "100px",
    isStandaloneName: "80px",
    agreementNumber: "100px",
    prolongTypeName: "100px",
    lifespanPeriodName: "100px",
    period: "180px",
    tariffModelName: "90px",
    currencyName: "70px",
    priceUnitName: "70px",
    periodTypeName: "70px",
    periodAlignmentName: "70px",
    serviceProfileName: "70px",
    paymentTypeName: "70px",
    invoicePeriodName: "70px",
  };

  const getColumns = (): TableColumn<TableData>[] => [
    {
      key: "id",
      dataIndex: "id",
      title: "ID",
      hidden: true,
      width: columnsWidths.id,
    },
    {
      key: "extName",
      dataIndex: "extName",
      title: "Наименование",
      hidden: false,
      width: columnsWidths.extName,
    },
    {
      key: "name",
      dataIndex: "name",
      title: "Код",
      hidden: false,
      width: columnsWidths.name,
    },
    {
      key: "offeringTypeName",
      dataIndex: "offeringTypeName",
      title: "Тип",
      hidden: true,
      width: columnsWidths.offeringTypeName,
    },
    {
      key: "offeringStatusName",
      dataIndex: "offeringStatusName",
      title: "Статус",
      hidden: true,
      width: columnsWidths.offeringStatusName,
    },
    {
      key: "isStandaloneName",
      dataIndex: "isStandaloneName",
      title: "Доступ к подкл.",
      hidden: false,
      width: columnsWidths.isStandaloneName,
    },
    {
      key: "agreementNumber",
      dataIndex: "agreementNumber",
      title: "Договор",
      hidden: true,
      render: (_, { agreementNumber, agreementData, agreementId }) => {
        if (!agreementNumber) {
          return agreementId;
        }
        const title = agreementData
          .filter(Boolean)
          .map((item, idx) => <span key={idx}>{item}</span>);
        return (
          <Tooltip
            title={title}
            overlayClassName="offer-list-table__agreementData"
            placement="topLeft"
          >
            {agreementNumber}
          </Tooltip>
        );
      },
      width: columnsWidths.agreementNumber,
    },
    {
      key: "prolongTypeName",
      dataIndex: "prolongTypeName",
      title: "Тип пролонгации",
      hidden: false,
      width: columnsWidths.prolongTypeName,
    },
    {
      key: "scrollMarkIdx",
      constantlyDisplayed: true,
      width: 10,
      render: (v, r, idx) => {
        if (scrollMarkIdx === idx) {
          return <div ref={scrollingRef} />;
        }
      },
    },
    {
      key: "lifespanPeriodName",
      dataIndex: "lifespanPeriodName",
      title: "Период продукта",
      hidden: true,
      width: columnsWidths.lifespanPeriodName,
    },
    {
      key: "period",
      dataIndex: "period",
      title: "Период",
      hidden: false,
      width: columnsWidths.period,
    },
    {
      key: "tariffModelName",
      dataIndex: "tariffModelName",
      title: "Бизнес-модель",
      hidden: false,
      width: columnsWidths.tariffModelName,
    },
    {
      key: "serviceProfileName",
      dataIndex: "serviceProfileName",
      title: "Профиль оплаты",
      hidden: false,
      width: columnsWidths.serviceProfileName,
    },
    {
      key: "paymentTypeName",
      dataIndex: "paymentTypeName",
      title: "Тип оплаты",
      hidden: false,
      width: columnsWidths.paymentTypeName,
    },
    {
      key: "invoicePeriodName",
      dataIndex: "invoicePeriodName",
      title: "Расчетный период",
      hidden: true,
      width: columnsWidths.invoicePeriodName,
    },
  ];

  const [columns, setColumns] = useState<TableColumn<TableData>[]>(getColumns());

  // Обновление колонок при изменении индекса строки для добавление ref для подгрузки по скроллу
  useEffect(() => {
    setColumns(getColumns());
  }, [scrollMarkIdx]);

  const toggleOfferSelection = (params: { id?: number; fromDate?: string }) => {
    const { id, fromDate } = params;
    if (selectedOffer?.id === id && selectedOffer?.fromDate === fromDate) {
      setSelectedOffer(undefined);
    } else {
      const currentOffer = offerList.find(
        (offerItem) => offerItem.id === id && offerItem.fromDate === fromDate
      );
      setSelectedOffer(currentOffer);
    }
  };

  const toggleProductOfferSelection = (params: { id?: number; fromDate?: string }) => {
    const { id, fromDate } = params;
    if (selectedOffer?.id === id && selectedOffer?.fromDate === fromDate) {
      setSelectedOffer(undefined);

      if (setOfferListSelectMode && offerListSelectMode === "basedOn") {
        setOfferListSelectMode && setOfferListSelectMode("select");
      }
    } else {
      const currentOffer = offerList.find(
        (offerItem) => offerItem.id === id && offerItem.fromDate === fromDate
      );

      if (
        setOfferListSelectMode &&
        offerListSelectMode === "basedOn" &&
        currentOffer &&
        currentOffer.productCharValues &&
        currentOffer.productCharValues.length > 0
      ) {
        setOfferListSelectMode("select");
      }

      setSelectedOffer(currentOffer);
    }
  };

  const onRow: TableProps<TableData>["onRow"] = ({ id, fromDate, toDate }) => ({
    onClick: () => {
      switch (offerListViewMode) {
        case "createOffer":
          if (offerListSelectMode === "basedOn") {
            toggleOfferSelection({ id, fromDate });
          }
          break;
        case "selectOffer":
          toggleOfferSelection({ id, fromDate });
          break;
        case "addTemplate":
          toggleOfferSelection({ id, fromDate });
          break;
        case "selectOfferAdd":
          toggleOfferSelection({ id, fromDate });
          break;
        case "view":
          if (fromDate) {
            // Если дата поиска попадает между датами версии - берем ее, иначе fromDate версии
            const onDateString = searchListValues.onDate.isBetween(fromDate, toDate)
              ? toHttpFormat(searchListValues.onDate)
              : toHttpFormat(fromDate);
            history.push(`/offer/${id}?onDate=${onDateString}`);
          }
          break;
        case "createProduct":
          if (offerListSelectMode === "basedOn" || offerListSelectMode === "select") {
            toggleProductOfferSelection({ id, fromDate });
          }
          break;
        default:
          break;
      }
    },
  });

  return (
    <div className="offer-list-table">
      <Row justify="end" style={{ marginTop: "10px" }}>
        <ToggleColumns
          tableName={`offer-list-table-${filterName}`}
          columns={columns}
          setColumns={setColumns}
        />
      </Row>
      <Table
        dataSource={tableData}
        columns={columns.filter(({ hidden }) => !hidden)}
        loading={isTableLoading}
        pagination={false}
        size="small"
        scroll={{
          y: 400,
          x: "700px",
        }}
        rowClassName={({ key }) =>
          key === `${selectedOffer?.id}_${selectedOffer?.fromDate}` ? "ant-table-row-selected" : ""
        }
        onRow={onRow}
      />
    </div>
  );
};
